import { useEffect, useRef } from 'react';

// Частота обновления индикатора: чаще глазу не нужно
const FRAME_MS = 60;

// Уровень сигнала 0..1 по аудиодорожке: захват до подключения, выход шумодава в эфире.
// Отдаем в колбэк, а не в состояние React: иначе блок настроек перерисовывался бы 16 раз
// в секунду весь эфир. Анализатор живет по треку, колбэк берем последний, как в useResizeObserver
export function useMicLevel(
  track: MediaStreamTrack | null,
  onLevel: (level: number) => void,
): void {
  const refOnLevel = useRef(onLevel);
  refOnLevel.current = onLevel;

  useEffect(() => {
    const emit = (level: number) => refOnLevel.current(level);
    if (!track || typeof AudioContext === 'undefined') {
      emit(0);
      return;
    }

    const ctx = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    const source = ctx.createMediaStreamSource(new MediaStream([track]));
    source.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);

    // Контекст, созданный без жеста, спит: будим по первому клику
    const wake = () => ctx.resume().catch(() => {});
    if (ctx.state !== 'running') document.addEventListener('pointerdown', wake, { once: true });

    let raf = 0;
    let last = 0;
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      if (now - last < FRAME_MS) return;
      last = now;
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i]! - 128) / 128;
        sum += v * v;
      }
      // RMS тихого голоса около 0.05: растягиваем, чтобы шкала жила
      emit(Math.min(1, Math.sqrt(sum / data.length) * 4));
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('pointerdown', wake);
      source.disconnect();
      ctx.close().catch(() => {});
      emit(0);
    };
  }, [track]);
}
